/* Announcement bar controller
   Contract with sections/announcement-bar.liquid:
   - root: .custom-announcement-bar[data-announcement-bar][data-section-id][data-sticky][data-dismiss]
   - the painted bar is the <nav class="topbar"> inside it
   - TEXT ROTATOR: [data-ab-rotator] holds .ab-rotator__item children (one per announcement). ONLY the text rotates —
     the countdown and the DEALS CTA sit outside it and never move. data-autoplay / data-speed (seconds).
     EDGE CONTROLS (children of the nav): [data-ab-prev] / [data-ab-next] step through the announcements (the new
     text is announced to screen readers via [data-ab-live]); [data-ab-motion-toggle] is the WCAG 2.2.2 pause/play,
     which also slows the countdown to one update per minute. The pause choice persists for the session; Reduce
     Motion users start paused. Rotation also pauses while the pointer is in the bar, while a bar control has
     KEYBOARD focus (a mouse click doesn't freeze it), and while the dropdown is open.
   - DROPDOWN TRIGGERS: any .announcement-dropdown-toggle[aria-controls] (the CTA / chevron), plus the text of any
     rotator item with data-dropdown="<popup id>" (tapping the text does what the CTA does).
   - the sheet is .notification-banner-popup (role=dialog). While it is open:
       * the THEME's page regions are inert (sections, main, header, footer, the skip link). Third-party UI that is
         appended straight to <body> — Shop Pay's installments popup, OneTrust's cookie banner and preference
         centre, chat widgets, accessibility toolbars — is deliberately left LIVE, so a shopper can always use it.
         (Inerting every body child froze exactly those, which is why their close buttons stopped working.)
       * containment comes from inert, not from a JavaScript Tab trap: Tab moves through the bar, the sheet and any
         live third-party overlay, then out to the browser — the same as a native <dialog>. A JS trap would stop
         keyboard users reaching a cookie banner that sits on top of the sheet. Browsers without inert (very old)
         get the old trap as a fallback.
       * no aria-modal: inert already hides the page from assistive tech, and aria-modal would additionally hide
         the live overlays (iOS VoiceOver confines navigation to an aria-modal dialog).
       * a bottom overlay (OneTrust's banner) is measured and published as --nb-obscured-bottom, so the sheet adds
         that much scroll room and keeps focused controls clear of it (WCAG 2.4.11 Focus Not Obscured).
       * body scroll is locked, Escape closes (never while focus is in, or the key came from, another dialog), and
         focus returns to the trigger.
   - REWARDS COLUMN: [data-ab-rewards][data-col][data-state=guest|member]. Its Join button is Shopify's
     <shopify-account data-ab-rewards-join> (sign-in sheet on the page). While that sheet is open, Escape stands
     aside. When the sheet closes, this section is re-fetched (Section Rendering API); if the shopper is now signed
     in, the member view replaces the guest view and focus moves to its heading.
   - STICKY BAR: publishes its height (header offset) and, when the page has no scroll-padding of its own, sets
     html scroll-padding-top to bar + pinned header, so Tab never scrolls a focused element under them (2.4.11).
   - Sale countdown: any [data-sale-countdown="<ISO date>"] element gets its [data-cd-d/h/m/s] children ticked
   Everything lives here (not inline in the section) so it re-initialises when the theme editor re-renders the section. */
(function () {
  'use strict';

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var INERT_SUPPORTED = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;

  // shopify-account is included: its sign-in button lives in its shadow root, so the host is the tab stop we see.
  var FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]),'
    + ' input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]),'
    + ' textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]), shopify-account';

  // The theme's own page regions. Only these are made inert at <body> level while the sheet is open; anything else
  // a third party appended to <body> (consent banners, Shop Pay's popup, chat, accessibility toolbars) stays usable.
  // A body child IS a theme region when it is one of these (Shopify's section wrappers, the page container that
  // holds <main> and the footer group, the skip link, or a bare landmark the layout itself put in <body>)…
  var THEME_REGION = '.shopify-section, [id^="shopify-section-"], #PageContainer, .page-container, #MainContent,'
    + ' main, [role="main"], header, footer, .skip-link, .skip-to-content-link, a[href="#MainContent"]';
  // …or when it CONTAINS a Shopify section wrapper (the page container does). Bare header/footer/main are NOT
  // used for this contains-test: a chat, consent or toolbar root with its own <header> inside is a third-party
  // overlay and must stay live, which is the whole point of the non-modal design.
  var THEME_REGION_INSIDE = '.shopify-section, [id^="shopify-section-"]';

  // Fixed overlays anchored to the bottom of the screen that can sit on top of the sheet (OneTrust's banner).
  var BOTTOM_OVERLAYS = '#onetrust-banner-sdk';

  function px(n) { return Math.round(n) + 'px'; }

  function isKeyboardFocus(el) {
    try { return el.matches(':focus-visible'); } catch (e) { return true; }
  }

  // Focus an element, reaching into an open shadow root when the host itself can't take focus (shopify-account).
  function focusStop(el) {
    if (!el) return;
    el.focus({ preventScroll: false });
    if (document.activeElement !== el && el.shadowRoot) {
      var inner = el.shadowRoot.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
      if (inner) inner.focus();
    }
  }

  /* ---------------------------------------------------------------- motion control (WCAG 2.2.2) ---- */

  var MOTION_KEY = 'ab-motion-paused';
  var motionPaused = reduceMotion;
  try {
    var storedMotion = sessionStorage.getItem(MOTION_KEY);
    if (storedMotion !== null) motionPaused = storedMotion === '1';
  } catch (e) {}

  var rotators = [];   // { root, hold(reason, bool), select(el), destroy() }
  var countdowns = []; // { el, reschedule(), stop() }

  function syncMotionToggles() {
    document.documentElement.classList.toggle('ab-motion-paused', motionPaused);
    document.querySelectorAll('[data-ab-motion-toggle]').forEach(function (btn) {
      btn.setAttribute('data-paused', motionPaused ? 'true' : 'false');
      var label = btn.getAttribute(motionPaused ? 'data-label-play' : 'data-label-pause');
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  function setMotionPaused(value) {
    motionPaused = !!value;
    try { sessionStorage.setItem(MOTION_KEY, motionPaused ? '1' : '0'); } catch (e) {}
    syncMotionToggles();
    rotators.forEach(function (r) { r.hold('user', motionPaused); });
    countdowns.forEach(function (c) { c.reschedule(); });
  }

  function bindMotionToggle(btn) {
    if (btn.dataset.abBound) return;
    btn.dataset.abBound = '1';
    btn.addEventListener('click', function () { setMotionPaused(!motionPaused); });
  }

  /* ---------------------------------------------------------------- bar: height, sticky, dismiss ---- */

  var bars = new Map(); // sectionId -> teardown()
  // Pinned height per bar instance. Several announcement-bar sections can live in the header group at once (the
  // theme shows/hides them per Abra promo code), so the header offset is the SUM of every instance's rendered
  // height — a hidden instance measures 0 and can no longer overwrite the visible bar's value with 0.
  var pinnedHeights = new Map(); // sectionId -> px number
  function totalPinned() {
    var t = 0;
    pinnedHeights.forEach(function (v) { t += v; });
    return t;
  }
  // True while the dropdown holds the page scroll lock (see lockScroll): bar geometry is frozen meanwhile and every
  // registered measurer is re-run once the lock lifts and layout has settled.
  var scrollLocked = false;
  var headerTopOwned = false;  // true while the header wrapper's inline `top` was written by this script
  var remeasurers = new Set(); // publishHeight() of every live bar instance
  function remeasureAll() {
    remeasurers.forEach(function (fn) { try { fn(); } catch (e) {} });
  }

  // After the bar is dismissed its button disappears; hand focus to the next thing on the page instead of <body>.
  function focusAfter(host) {
    var all = document.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (host.contains(el)) continue;
      if (!(host.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (!el.getClientRects().length || el.closest('[inert]')) continue;
      focusStop(el);
      return;
    }
  }

  function initBar(root) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;
    if (bars.has(sectionId)) bars.get(sectionId)();

    var host = document.getElementById('shopify-section-' + sectionId) || root;
    var nav = root.querySelector('.topbar');
    if (!nav) return;

    var sticky = root.getAttribute('data-sticky') === 'true';
    var dismissible = root.getAttribute('data-dismiss') === 'true';
    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    var teardown = [];
    var htmlEl = document.documentElement;

    // The theme's sticky header wrapper (.header-section). Some builds of theme.js give it an inline
    // `top: var(--variant-bar-height) !important`, others leave it at the stylesheet's top: 0 — so publish() both
    // sets the variable (on :root and the wrapper) AND writes the wrapper's inline `top` while a bar is pinned.
    var headerWrap = document.querySelector('.shopify-section-group-header-group.header-section')
                  || document.querySelector('.header-section');

    // Focus Not Obscured (2.4.11): with the bar pinned on top of a sticky header, keyboard focus could scroll under
    // both. Manage html scroll-padding-top ONLY if the page doesn't already set one (or we set it earlier).
    var ownsScrollPad = false;
    if (sticky) {
      if (htmlEl.dataset.abScrollPad === '1') {
        ownsScrollPad = true;
      } else {
        var sp = getComputedStyle(htmlEl).scrollPaddingTop;
        ownsScrollPad = !sp || sp === 'auto' || parseFloat(sp) === 0;
      }
      if (ownsScrollPad) htmlEl.dataset.abScrollPad = '1';
    }

    // Layout is READ in one pass (measure) and WRITTEN in a second (apply), and every write is skipped when the
    // value hasn't changed — so a scroll frame never interleaves reads and writes (no forced synchronous layout).
    var lastPublished = null, lastScrollPad = null;

    function measure() {
      var m = { dismissed: host.classList.contains('is-dismissed'), barH: 0, barBottom: 0, offset: 0, headerPinned: false, headerTop: 0, headerH: 0, y: window.scrollY || 0 };
      if (sticky && !m.dismissed) {
        var r = nav.getBoundingClientRect();
        m.barH = r.height;
        m.barBottom = r.bottom;
        // Header offset = the bar's rendered bottom edge while it is pinned at the viewport top, floored so a
        // fractional height overlaps the header by <1px (hidden under the bar) instead of leaving a hairline gap.
        // Any other reading (bar scrolled, mid-layout, a stale rect right after the page's scroll lock lifts) falls
        // back to the plain height — always a sane value. Nothing here accumulates from frame to frame: an earlier
        // "seam correction" that integrated the header/bar gap over scroll frames could latch at its cap after one
        // bad measurement and park the header 200px down the page.
        m.offset = (r.top > -1 && r.top < 1) ? Math.floor(r.bottom) : Math.round(r.height);
      }
      if (headerWrap) {
        var pos = getComputedStyle(headerWrap).position;
        if (pos === 'sticky' || pos === 'fixed') {
          var hr = headerWrap.getBoundingClientRect();
          m.headerPinned = true;
          m.headerTop = hr.top;
          m.headerH = hr.height;
        }
      }
      return m;
    }
    function publish(h) {
      pinnedHeights.set(sectionId, Math.max(0, h));
      var total = totalPinned();
      var v = px(total);
      // The header must sit flush under the pinned bar, so its sticky `top` is written DIRECTLY onto the header
      // wrapper (inline, !important) whenever a bar is pinned and the header is sticky/fixed — the variable alone
      // is not enough: on the live theme nothing maps --variant-bar-height into the header's `top`, so the header
      // pinned at 0 and slid under the bar on scroll. The inline top is removed again when no bar is pinned.
      var pinHeader = false;
      if (headerWrap && total > 0) {
        var hp = getComputedStyle(headerWrap).position;
        pinHeader = hp === 'sticky' || hp === 'fixed';
      }
      // Skip the write when nothing changed — checked against the live inline values (a cheap style read, not a
      // layout read), so a later write by the theme's own scripts to --variant-bar-height or top is still corrected.
      var same = v === lastPublished
        && htmlEl.style.getPropertyValue('--topbar-height') === v
        && (!headerWrap || headerWrap.style.getPropertyValue('--variant-bar-height') === v)
        && (!pinHeader || headerWrap.style.getPropertyValue('top') === v)
        && (pinHeader || !headerTopOwned);
      if (same) return;
      lastPublished = v;
      htmlEl.style.setProperty('--topbar-height', v);
      htmlEl.style.setProperty('--variant-bar-height', v);
      if (headerWrap) {
        headerWrap.style.setProperty('--variant-bar-height', v);
        if (pinHeader) {
          headerWrap.style.setProperty('top', v, 'important');
          headerTopOwned = true;
        } else if (headerTopOwned) {
          headerWrap.style.removeProperty('top');
          headerTopOwned = false;
        }
      }
    }
    function applyScrollPad(m) {
      if (!ownsScrollPad) return;
      var v = px(m.barH + (m.headerPinned ? m.headerH : 0) + 8);
      if (v === lastScrollPad) return;
      lastScrollPad = v;
      htmlEl.style.scrollPaddingTop = v;
    }
    function apply(m) {
      publish(m.offset);
      applyScrollPad(m);
    }
    // While the dropdown has the page scroll-locked (<body> pinned with position: fixed), sticky geometry is not
    // trustworthy — Safari can report stale rects for a frame — so nothing is measured or published until the lock
    // lifts; unlockScroll() re-runs every bar's measurement once layout has settled.
    function publishHeight() { if (!scrollLocked) apply(measure()); }
    // Post-unlock resync: publish the height now and re-run the scroll pass (is-stuck state) on the next frame.
    var resync = function () { publishHeight(); if (typeof schedule === 'function') schedule(); };
    remeasurers.add(resync);
    teardown.push(function () { remeasurers.delete(resync); });
    publishHeight();
    if (ownsScrollPad) {
      teardown.push(function () { htmlEl.style.scrollPaddingTop = ''; delete htmlEl.dataset.abScrollPad; });
    }
    // Teardown (section removed or re-rendered in the editor): nothing else must keep reserving the old bar
    // height — publish 0 and clear the header wrapper's copy of the variable.
    teardown.push(function () {
      publish(0);
      pinnedHeights.delete(sectionId);
      // Only clear the header wrapper's copies when no other bar instance is still pinned.
      if (headerWrap && !pinnedHeights.size) {
        headerWrap.style.removeProperty('--variant-bar-height');
        if (headerTopOwned) { headerWrap.style.removeProperty('top'); headerTopOwned = false; }
      }
    });
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(publishHeight);
      ro.observe(nav);
      teardown.push(function () { ro.disconnect(); });
    }
    window.addEventListener('load', publishHeight);
    teardown.push(function () { window.removeEventListener('load', publishHeight); });

    // Dismiss (session-scoped; ignored in the editor so the section never vanishes while being edited)
    if (dismissible) {
      var KEY = 'ab-dismissed-' + sectionId;
      var dismiss = function (persist) {
        host.classList.add('is-dismissed');
        publishHeight();
        if (persist) { try { sessionStorage.setItem(KEY, '1'); } catch (e) {} }
      };
      try { if (!inEditor && sessionStorage.getItem(KEY) === '1') dismiss(false); } catch (e) {}
      root.querySelectorAll('[data-announcement-dismiss]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var hadFocus = document.activeElement === btn;
          dismiss(true);
          if (hadFocus) focusAfter(host);
        });
      });
    }

    var ticking = false;
    var update;
    if (sticky) {
      update = function () {
        ticking = false;
        if (scrollLocked) return;
        // reads
        var m = measure();
        var stuck = m.y > 4;
        // writes
        if (nav.classList.contains('is-stuck') !== stuck) nav.classList.toggle('is-stuck', stuck);
        apply(m);
      };
    } else {
      update = function () {
        ticking = false;
        if (scrollLocked) return;
        var m = measure();
        if (m.headerPinned && m.y > 4 && m.headerTop > 0.5) {
          var el = document.elementFromPoint(Math.round(window.innerWidth / 2), 1);
          var legit = false;
          for (var n = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
            if (n === headerWrap) { legit = true; break; }
            var p = getComputedStyle(n).position;
            if (p === 'fixed' || p === 'sticky') { legit = true; break; }
          }
          if (!legit) publish(0);
        }
      };
    }
    var schedule = function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule);
    teardown.push(function () {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
    });
    update();
    requestAnimationFrame(update);

    bars.set(sectionId, function () {
      teardown.forEach(function (fn) { fn(); });
      bars.delete(sectionId);
    });
  }

  /* ---------------------------------------------------------------- text rotator ---- */

  function initRotator(root) {
    if (root.dataset.abBound) return;
    root.dataset.abBound = '1';
    var items = Array.prototype.slice.call(root.querySelectorAll('.ab-rotator__item'));
    var nav = root.closest('.topbar');
    // The whole bar (text, CTA, edge controls) is the hover / focus zone.
    var bar = nav || root.closest('.announcement-bar') || root.parentElement;
    var live = nav ? nav.querySelector('[data-ab-live]') : null;
    var idx = Math.max(0, items.findIndex(function (it) { return it.classList.contains('is-active'); }));
    if (items.length < 2) return;

    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var speed = Math.max(3, parseFloat(root.getAttribute('data-speed')) || 5) * 1000;
    var timer = null;
    // Reasons the rotation is paused: user (pause button / Reduce Motion), hover, focus, sheet, editor.
    var holds = { user: motionPaused };
    var LEAVE_MS = reduceMotion ? 0 : 460;

    function held() { for (var k in holds) { if (holds[k]) return true; } return false; }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() { stop(); if (autoplay && !held()) timer = setInterval(function () { go(idx + 1); }, speed); }
    function hold(reason, on) { holds[reason] = on; if (held()) stop(); else start(); }

    function go(n) {
      n = (n % items.length + items.length) % items.length;
      if (n === idx) return;
      var cur = items[idx], next = items[n];
      cur.classList.remove('is-active');
      cur.classList.add('is-leaving');
      cur.setAttribute('aria-hidden', 'true');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      setTimeout(function () { cur.classList.remove('is-leaving'); }, LEAVE_MS);
      idx = n;
    }

    // After a MANUAL change (arrow or swipe) tell screen-reader users what the bar says now. innerText skips the
    // hidden mobile/desktop variant, so only the text actually on screen is read.
    function announceCurrent() {
      if (!live) return;
      var text = (items[idx].innerText || items[idx].textContent || '').replace(/\s+/g, ' ').trim();
      live.textContent = '';
      requestAnimationFrame(function () { live.textContent = text; });
    }
    function step(dir) { go(idx + dir); start(); announceCurrent(); }

    var onPrev = function () { step(-1); };
    var onNext = function () { step(1); };
    var prevBtns = nav ? Array.prototype.slice.call(nav.querySelectorAll('[data-ab-prev]')) : [];
    var nextBtns = nav ? Array.prototype.slice.call(nav.querySelectorAll('[data-ab-next]')) : [];
    prevBtns.forEach(function (b) { b.addEventListener('click', onPrev); });
    nextBtns.forEach(function (b) { b.addEventListener('click', onNext); });

    // Pause while the shopper is reading or using the bar. Focus only holds for KEYBOARD focus — a mouse click on
    // an arrow or the CTA must not leave the rotation frozen until the shopper clicks somewhere else.
    var onEnter = function () { hold('hover', true); };
    var onLeave = function () { hold('hover', false); };
    var onFocusIn = function (e) { if (isKeyboardFocus(e.target)) hold('focus', true); };
    var onFocusOut = function (e) { if (!bar.contains(e.relatedTarget)) hold('focus', false); };
    bar.addEventListener('mouseenter', onEnter);
    bar.addEventListener('mouseleave', onLeave);
    bar.addEventListener('focusin', onFocusIn);
    bar.addEventListener('focusout', onFocusOut);

    // Swipe on phones. A swipe marks the rotator as "swiping" briefly so the tap that ends it doesn't open the sheet.
    var sx = 0, sy = 0;
    root.addEventListener('touchstart', function (e) { var t = e.touches[0]; sx = t.clientX; sy = t.clientY; }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var t = e.changedTouches[0];
      var dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        root.dataset.swiping = '1';
        setTimeout(function () { delete root.dataset.swiping; }, 350);
        step(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    var entry = {
      root: root,
      hold: hold,
      select: function (el) { var i = items.indexOf(el); if (i > -1) { hold('editor', true); go(i); return true; } return false; },
      destroy: function () {
        stop();
        prevBtns.forEach(function (b) { b.removeEventListener('click', onPrev); });
        nextBtns.forEach(function (b) { b.removeEventListener('click', onNext); });
        bar.removeEventListener('mouseenter', onEnter);
        bar.removeEventListener('mouseleave', onLeave);
        bar.removeEventListener('focusin', onFocusIn);
        bar.removeEventListener('focusout', onFocusOut);
      }
    };
    rotators.push(entry);
    start();
  }

  // Hold every rotator while a dropdown is open, so the text you tapped is still there when you close it.
  document.addEventListener('notificationBanner:shown', function () { rotators.forEach(function (r) { r.hold('sheet', true); }); });
  document.addEventListener('notificationBanner:hidden', function () { rotators.forEach(function (r) { r.hold('sheet', false); }); });

  // Theme editor: clicking an announcement block in the sidebar shows that text and holds it there.
  document.addEventListener('shopify:block:select', function (e) {
    rotators.forEach(function (r) { if (document.contains(r.root)) r.select(e.target); });
  });
  document.addEventListener('shopify:block:deselect', function () {
    rotators.forEach(function (r) { r.hold('editor', false); });
  });

  /* ---------------------------------------------------------------- rewards column ---- */

  // True while Shopify's account (sign-in) sheet is open. The component fires `open` / `close` on its host element;
  // capture-phase listeners on document catch them whether or not they bubble.
  var accountSheetOpen = false;

  // After the sign-in sheet closes, re-render this section and swap in the member view if the shopper signed in.
  // Harmless if they just closed the sheet (the fresh markup is still the guest view, so nothing changes), and
  // harmless if Shopify reloads the page after sign-in anyway.
  function refreshRewards(host) {
    var col = host.closest('[data-ab-rewards]');
    var root = host.closest('[data-announcement-bar]');
    if (!col || !root) return;
    var sid = root.getAttribute('data-section-id');
    var key = col.getAttribute('data-col');
    if (!sid || !key) return;
    fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sid), { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-ab-rewards][data-col="' + key + '"]');
        if (!next || next.getAttribute('data-state') !== 'member' || !document.contains(col)) return;
        var fresh = document.importNode(next, true);
        col.replaceWith(fresh);
        // Screen readers hear "Welcome back, …" — but only move focus if the dropdown is still open.
        var heading = fresh.querySelector('[data-ab-rewards-heading]');
        if (heading && fresh.closest('.notification-banner-popup.show')) heading.focus({ preventScroll: true });
      })
      .catch(function () {});
  }

  document.addEventListener('open', function (e) {
    if (e.target && e.target.tagName === 'SHOPIFY-ACCOUNT') accountSheetOpen = true;
  }, true);
  document.addEventListener('close', function (e) {
    var host = e.target;
    if (!host || host.tagName !== 'SHOPIFY-ACCOUNT') return;
    accountSheetOpen = false;
    if (host.hasAttribute('data-ab-rewards-join')) refreshRewards(host);
  }, true);

  /* ---------------------------------------------------------------- notification sheet ---- */

  var active = null;
  var lastTrigger = null;
  var hideTimer = null;
  var trapHandler = null;
  var lockedScrollY = 0;

  // Paint order while the sheet is open. The sheet is z-28 so the theme's drawers/modals (z-30+) can always cover
  // it, and the header section is expected at z-20. If a theme build gives the header a higher z-index than the
  // sheet, the header would paint over the top of the open sheet — so for the sheet's lifetime the header is held
  // just beneath it (inline, restored verbatim on close). It is inert meanwhile anyway, so only paint changes.
  var headerZ = null; // { el, value, priority } while patched
  function holdHeaderUnderSheet() {
    var hw = document.querySelector('.shopify-section-group-header-group.header-section') || document.querySelector('.header-section');
    if (!hw) return;
    var z = parseInt(getComputedStyle(hw).zIndex, 10);
    if (isNaN(z) || z < 28) return;
    headerZ = { el: hw, value: hw.style.getPropertyValue('z-index'), priority: hw.style.getPropertyPriority('z-index') };
    hw.style.setProperty('z-index', '27', 'important');
  }
  function releaseHeaderZ() {
    if (!headerZ) return;
    if (headerZ.value) headerZ.el.style.setProperty('z-index', headerZ.value, headerZ.priority);
    else headerZ.el.style.removeProperty('z-index');
    headerZ = null;
  }
  var inertedNodes = [];
  var hiddenNodes = []; // no-inert fallback: nodes we set aria-hidden on (never ones that already carried it)
  var overlayWatch = null; // { ro, mo, onResize } while the sheet is open

  function setExpanded(popup, value) {
    document.querySelectorAll('.announcement-dropdown-toggle[aria-controls="' + popup.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', value ? 'true' : 'false');
    });
  }

  // iOS Safari ignores `overflow: hidden` on body while a fixed-position sheet is open — pin body instead.
  var unlockRaf = 0;
  function lockScroll() {
    // A lock requested while a previous unlock is still settling (one sheet replacing another) keeps the freeze on.
    if (unlockRaf) { cancelAnimationFrame(unlockRaf); unlockRaf = 0; }
    scrollLocked = true;
    if (document.body.classList.contains('ab-scroll-locked')) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = px(-lockedScrollY);
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.classList.add('ab-scroll-locked');
  }
  function unlockScroll() {
    if (!document.body.classList.contains('ab-scroll-locked')) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.classList.remove('ab-scroll-locked');
    window.scrollTo(0, lockedScrollY);
    // Keep the bars frozen through the restore: the scroll event from scrollTo() and Safari's first post-unpin frame
    // can both carry stale sticky geometry. Measure again two frames later, when layout has settled.
    if (unlockRaf) cancelAnimationFrame(unlockRaf);
    unlockRaf = requestAnimationFrame(function () {
      unlockRaf = requestAnimationFrame(function () {
        unlockRaf = 0;
        scrollLocked = false;
        remeasureAll();
      });
    });
  }

  function isThemeRegion(el) {
    try { return el.matches(THEME_REGION) || !!el.querySelector(THEME_REGION_INSIDE); } catch (e) { return false; }
  }

  // Everything outside the announcement bar that belongs to the THEME is made inert while the sheet is open (so
  // screen readers can't swipe out of it and Tab can't wander into the page behind). Inside the theme's wrappers
  // every sibling is inerted; at <body> level only theme regions are — third-party roots appended to <body> stay live.
  function inertBackground(popup) {
    releaseInert();
    var node = popup.closest('[data-announcement-bar]') || popup;
    while (node && node.parentElement && node !== document.body) {
      var parent = node.parentElement;
      var atBody = parent === document.body;
      for (var i = 0; i < parent.children.length; i++) {
        var el = parent.children[i];
        if (el === node) continue;
        var tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
        if (el.id === 'cart-live-region' || el.hasAttribute('inert') || el.contains(popup)) continue;
        if (atBody && !isThemeRegion(el)) continue;
        el.setAttribute('inert', '');
        inertedNodes.push(el);
        // Browsers without inert only get the Tab trap; hide the background from screen-reader browse mode too.
        if (!INERT_SUPPORTED && !el.hasAttribute('aria-hidden')) {
          el.setAttribute('aria-hidden', 'true');
          hiddenNodes.push(el);
        }
      }
      node = parent;
    }
  }
  function releaseInert() {
    inertedNodes.forEach(function (el) { el.removeAttribute('inert'); });
    hiddenNodes.forEach(function (el) { el.removeAttribute('aria-hidden'); });
    inertedNodes = [];
    hiddenNodes = [];
  }

  // A bottom-anchored overlay (OneTrust's banner) can cover the lower part of the sheet. Publish how much of the
  // viewport it covers; the sheet adds that much scroll room and scroll-padding, so every control can be scrolled
  // above it and keyboard focus is never left underneath it (WCAG 2.4.11).
  function syncObscured(popup) {
    var h = 0;
    var vh = window.innerHeight;
    document.querySelectorAll(BOTTOM_OVERLAYS).forEach(function (el) {
      if (!el.getClientRects().length) return;
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.top >= vh || r.bottom < vh - 4) return; // not on screen, or not anchored to the bottom
      h = Math.max(h, vh - r.top);
    });
    popup.style.setProperty('--nb-obscured-bottom', px(h));
  }
  // The sheet hangs from the bar's bottom edge: --nb-top is that edge, FLOORED. A bar whose text wraps has a
  // fractional height (e.g. 2 × 16.25px lines + padding), and rounding it UP left a sub-pixel strip of page showing
  // between bar and sheet on 2x/3x phones. Flooring makes the sheet start ≤1px under the bar instead, and the bar
  // paints above the sheet (z 29 over 28), so the overlap is invisible. Re-anchored while open whenever the bar
  // resizes (orientation change, font swap, a longer announcement) so the seam can't reopen.
  function anchorSheet(popup, bar) {
    if (!bar) return;
    var v = Math.max(0, Math.floor(bar.getBoundingClientRect().bottom)) + 'px';
    if (popup.style.getPropertyValue('--nb-top') !== v) popup.style.setProperty('--nb-top', v);
  }
  function watchOverlays(popup) {
    unwatchOverlays();
    var bar = popup.closest('[data-announcement-bar]');
    bar = bar ? bar.querySelector('.topbar') : null;
    var sync = function () {
      if (active !== popup) return;
      anchorSheet(popup, bar);
      syncObscured(popup);
    };
    var ro = window.ResizeObserver ? new ResizeObserver(sync) : null;
    var observe = function () {
      if (!ro) return;
      if (bar) ro.observe(bar);
      document.querySelectorAll(BOTTOM_OVERLAYS).forEach(function (el) { ro.observe(el); });
    };
    observe();
    // The banner can be injected (or removed) after the sheet opens.
    var mo = window.MutationObserver ? new MutationObserver(function () { observe(); sync(); }) : null;
    if (mo) mo.observe(document.body, { childList: true, subtree: false, attributes: false });
    var consent = document.getElementById('onetrust-consent-sdk');
    if (mo && consent) mo.observe(consent, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', sync, { passive: true });
    overlayWatch = { ro: ro, mo: mo, onResize: sync };
    sync();
  }
  function unwatchOverlays() {
    if (!overlayWatch) return;
    if (overlayWatch.ro) overlayWatch.ro.disconnect();
    if (overlayWatch.mo) overlayWatch.mo.disconnect();
    window.removeEventListener('resize', overlayWatch.onResize);
    overlayWatch = null;
  }

  // Focus is somewhere legitimate OUTSIDE the sheet: a third-party overlay (Shop Pay's popup, a cookie banner), or
  // the bar's own controls. Escape (and the fallback trap) must not interfere there.
  function focusIsElsewhere(container) {
    var a = document.activeElement;
    if (!a || a === document.body || a === document.documentElement) return false;
    if (container.contains(a)) return false;
    return !a.closest('[inert]');
  }

  // True when the key came from inside ANOTHER dialog — including one rendered in a shadow root inside the sheet
  // (e.g. Shopify's Shop Pay popup, depending on how Shopify builds it). That dialog owns its own Escape.
  function fromOtherDialog(e, popup) {
    var path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (n === popup || n === document || n === window) break;
      if (!n || n.nodeType !== 1) continue;
      if (n.tagName === 'DIALOG' || n.getAttribute('aria-modal') === 'true') return true;
      var role = n.getAttribute('role');
      if (role === 'dialog' || role === 'alertdialog') return true;
    }
    return false;
  }

  // The real Tab stops, in order: visible, and only ONE stop per radio group (the checked one, else the first).
  function tabStops(container) {
    var seenGroups = {};
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      if (!el.getClientRects().length) return false;
      if (el.type === 'radio' && el.name) {
        if (seenGroups[el.name]) return false;
        var group = container.querySelectorAll('input[type="radio"]');
        var checked = null, first = null;
        for (var i = 0; i < group.length; i++) {
          if (group[i].name !== el.name) continue;
          if (!first) first = group[i];
          if (group[i].checked) { checked = group[i]; break; }
        }
        var stop = checked || first;
        if (el !== stop) return false;
        seenGroups[el.name] = true;
      }
      return true;
    });
  }

  // FALLBACK ONLY (browsers without inert): keep Tab inside the sheet. Re-queried on every Tab, because the
  // featured-product card replaces its DOM on a color swap. It stands aside for third-party overlays.
  function trapFocus(container) {
    var handler = function (e) {
      if (e.key !== 'Tab') return;
      if (accountSheetOpen) return;
      if (focusIsElsewhere(container)) return;
      var stops = tabStops(container);
      if (!stops.length) return;
      var first = stops[0], last = stops[stops.length - 1];
      var a = document.activeElement;
      if (!container.contains(a)) { e.preventDefault(); focusStop(e.shiftKey ? last : first); return; }
      if (e.shiftKey && (a === first || a === container)) { e.preventDefault(); focusStop(last); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); focusStop(first); }
    };
    document.addEventListener('keydown', handler);
    return handler;
  }

  // The dropdown's stylesheet loads deferred (media="print" + onload). If the sheet is opened before it has
  // arrived, apply it now and wait for it (briefly) rather than showing the sheet unstyled in normal flow.
  function withSheetCss(cb) {
    var link = document.querySelector('link[rel="stylesheet"][href*="announcement-banner-sheet"]');
    if (!link || (link.media === 'all' && link.sheet)) { cb(); return; }
    var done = false;
    var finish = function () { if (done) return; done = true; cb(); };
    link.addEventListener('load', finish, { once: true });
    link.addEventListener('error', finish, { once: true });
    if (link.media !== 'all') link.media = 'all';
    if (link.sheet) { finish(); return; }
    setTimeout(finish, 1500);
  }

  function show(popup, trigger) {
    if (active === popup) return;
    withSheetCss(function () { showNow(popup, trigger); });
  }

  function showNow(popup, trigger) {
    if (active === popup) return;
    if (active) hide(true);
    clearTimeout(hideTimer);

    active = popup;
    lastTrigger = trigger || null;

    // Anchor the sheet to the bar's real bottom edge (correct whether or not the bar is pinned)
    var bar = trigger ? trigger.closest('.topbar') : document.querySelector('.topbar');
    anchorSheet(popup, bar);

    popup.style.display = 'block';
    void popup.offsetHeight;
    popup.classList.add('show');
    popup.removeAttribute('aria-hidden');
    lockScroll();
    holdHeaderUnderSheet();
    inertBackground(popup);
    watchOverlays(popup);
    setExpanded(popup, true);

    var content = popup.querySelector('.notification-banner-content');
    trapHandler = (!INERT_SUPPORTED && content) ? trapFocus(content) : null;

    var closeBtn = popup.querySelector('.notification-banner-close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });

    document.dispatchEvent(new CustomEvent('notificationBanner:shown', { detail: { popup: popup } }));
  }

  // silent = true: don't hand focus back to the trigger (used when something else, e.g. the cart drawer,
  // is about to take focus).
  function hide(silent) {
    if (!active) return;
    var popup = active;
    active = null;

    popup.classList.remove('show');
    popup.setAttribute('aria-hidden', 'true');
    releaseInert();
    unwatchOverlays();
    popup.style.removeProperty('--nb-obscured-bottom');
    unlockScroll();
    releaseHeaderZ();
    if (trapHandler) { document.removeEventListener('keydown', trapHandler); trapHandler = null; }
    setExpanded(popup, false);

    hideTimer = setTimeout(function () { if (active !== popup) popup.style.display = 'none'; }, 300);

    if (!silent && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    lastTrigger = null;

    document.dispatchEvent(new CustomEvent('notificationBanner:hidden', { detail: { popup: popup } }));
  }

  function toggle(popup, trigger) { active === popup ? hide() : show(popup, trigger); }

  // The CTA / chevron.
  function bindToggle(btn) {
    if (btn.dataset.nbBound) return;
    var popup = document.getElementById(btn.getAttribute('aria-controls'));
    if (!popup) return;
    btn.dataset.nbBound = '1';
    btn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); toggle(popup, btn); });
  }

  // The announcement text: does exactly what the CTA does, instead of following the block link.
  function bindTextOpener(item) {
    if (item.dataset.nbBound) return;
    var popup = document.getElementById(item.getAttribute('data-dropdown'));
    var link = item.querySelector('.announcement__link');
    if (!popup || !link) return;
    item.dataset.nbBound = '1';
    link.style.cursor = 'pointer';
    // The CTA is the single keyboard stop for this action — no duplicate tab stop on the text. A screen reader's
    // browse cursor still reaches the text, so it says what it does: it opens a dialog, not a page.
    if (link.tagName === 'A') {
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', popup.id);
    }
    var rotator = item.closest('[data-ab-rotator]');
    link.addEventListener('click', function (e) {
      // A link authored INSIDE the rich text (e.g. a "Book a Demo" pill) keeps its own destination.
      var inner = e.target.closest && e.target.closest('a');
      if (inner && inner !== link && link.contains(inner)) return;
      e.preventDefault();
      e.stopPropagation();
      if (rotator && rotator.dataset.swiping) return; // the tap that ended a swipe
      var cta = item.closest('.announcement-bar') && item.closest('.announcement-bar').querySelector('.announcement-dropdown-toggle[aria-controls]');
      toggle(popup, cta || link);
    });
  }

  function bindPopup(popup) {
    if (popup.dataset.nbBound) return;
    popup.dataset.nbBound = '1';
    popup.setAttribute('aria-hidden', 'true');
    // Cached HTML from before this change may still carry aria-modal; see the header comment for why it's removed.
    popup.removeAttribute('aria-modal');
    var closeBtn = popup.querySelector('.notification-banner-close');
    if (closeBtn) closeBtn.addEventListener('click', function (e) { e.preventDefault(); hide(); });
    var overlay = popup.querySelector('.notification-banner-overlay');
    if (overlay) overlay.addEventListener('click', function (e) { if (e.target === overlay) hide(); });
  }

  /* ---------------------------------------------------------------- Sale/BFCM countdown ---- */

  // Ticks every second; while motion is paused it updates once a minute instead (WCAG 2.2.2 "control the
  // frequency"). The screen-reader text is not a live region and only changes when the minute changes.
  function initCountdown(el) {
    if (el.dataset.cdBound) return;
    el.dataset.cdBound = '1';
    var iso = el.getAttribute('data-sale-countdown');
    if (!iso) return;
    // The section normalises the target to full ISO 8601 WITH an offset (store time when none was given), so this
    // parses to the same instant for every visitor.
    var target = new Date(iso).getTime();
    var srEl = el.querySelector('[data-cd-sr]');
    var srTemplate = el.getAttribute('data-cd-sr-template') || 'Sale ends in [d] days, [h] hours, [m] minutes.';
    if (isNaN(target)) { el.style.display = 'none'; return; }

    var dEl = el.querySelector('[data-cd-d]'), hEl = el.querySelector('[data-cd-h]'),
        mEl = el.querySelector('[data-cd-m]'), sEl = el.querySelector('[data-cd-s]');
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var timer = null;
    var lastMinuteKey = null;

    function render() {
      var diff = target - Date.now();
      if (diff <= 0) {
        el.style.display = 'none';
        if (srEl) srEl.textContent = '';
        return false;
      }
      var s = Math.floor(diff / 1000);
      var d = Math.floor(s / 86400); s -= d * 86400;
      var h = Math.floor(s / 3600); s -= h * 3600;
      var m = Math.floor(s / 60); s -= m * 60;
      if (dEl) dEl.textContent = pad(d);
      if (hEl) hEl.textContent = pad(h);
      if (mEl) mEl.textContent = pad(m);
      if (sEl) sEl.textContent = pad(s);
      var minuteKey = d + ':' + h + ':' + m;
      if (srEl && minuteKey !== lastMinuteKey) {
        lastMinuteKey = minuteKey;
        srEl.textContent = srTemplate.replace('[d]', d).replace('[h]', h).replace('[m]', m);
      }
      return true;
    }

    function schedule() {
      clearTimeout(timer);
      if (!document.body.contains(el)) return;
      if (!render()) return;
      var now = Date.now();
      var delay = motionPaused ? 60000 - (now % 60000) : 1000 - (now % 1000);
      timer = setTimeout(schedule, delay + 20);
    }

    countdowns.push({ el: el, reschedule: schedule, stop: function () { clearTimeout(timer); } });
    schedule();
  }

  /* ---------------------------------------------------------------- init ---- */

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-announcement-bar]').forEach(initBar);
    scope.querySelectorAll('.notification-banner-popup').forEach(bindPopup);
    scope.querySelectorAll('.announcement-dropdown-toggle[aria-controls]').forEach(bindToggle);
    scope.querySelectorAll('.ab-rotator__item[data-dropdown]').forEach(bindTextOpener);
    scope.querySelectorAll('[data-ab-rotator]').forEach(initRotator);
    scope.querySelectorAll('[data-sale-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-ab-motion-toggle]').forEach(bindMotionToggle);
    syncMotionToggles();
  }

  // Escape closes the sheet — unless Shopify's sign-in sheet is open, the key came from inside another dialog, or
  // focus is in something outside the sheet (a cookie banner, Shop Pay's popup). Those handle their own Escape.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !active || e.defaultPrevented) return;
    if (accountSheetOpen) return;
    if (fromOtherDialog(e, active)) return;
    var content = active.querySelector('.notification-banner-content') || active;
    var a = document.activeElement;
    var inBar = a && a.closest && a.closest('[data-announcement-bar]');
    if (focusIsElsewhere(content) && !inBar) return;
    hide();
  });

  // Theme editor: re-run for the re-rendered section, and drop listeners for the one being removed
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    hide(true);
    var root = e.target && e.target.querySelector && e.target.querySelector('[data-announcement-bar]');
    var id = root && root.getAttribute('data-section-id');
    if (id && bars.has(id)) bars.get(id)();
    rotators = rotators.filter(function (r) {
      if (e.target.contains(r.root)) { r.destroy(); return false; }
      return true;
    });
    countdowns = countdowns.filter(function (c) {
      if (e.target.contains(c.el)) { c.stop(); return false; }
      return true;
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  window.notificationBannerPopup = {
    forceShow: function () {
      var p = document.querySelector('.notification-banner-popup');
      if (p) show(p);
    },
    hide: function (silent) { hide(!!silent); },
    reset: function () { hide(true); }
  };
})();
